import type {
  DirectCallSpeech,
  DirectCallSpeechAdapter,
} from './direct-call-playback';
import type { DirectCallDiagnostics } from './direct-call-diagnostics';
import type { DirectCallMessage } from './direct-call-session';

/** Uses accepted event identities only; never retains message text. */
export class DirectCallDiagnosticTurns {
  private readonly diagnostics: DirectCallDiagnostics;
  private readonly seenReplies = new Set<string>();
  private readonly now: () => number;
  private readonly interrupted: (error: unknown) => boolean;

  constructor(
    diagnostics: DirectCallDiagnostics,
    now: () => number = () => performance.now(),
    interrupted: (error: unknown) => boolean = () => false
  ) {
    this.diagnostics = diagnostics;
    this.now = now;
    this.interrupted = interrupted;
  }

  async send(
    session: {
      readonly messages: readonly DirectCallMessage[];
      send(
        text: string,
        to?: readonly string[],
        attachmentIds?: readonly string[]
      ): Promise<boolean>;
    },
    text: string,
    to?: readonly string[],
    attachmentIds: readonly string[] = []
  ): Promise<boolean> {
    const before = new Set(session.messages.map((message) => message.id));
    const started = this.now();
    const capture = this.diagnostics.takeCapture();
    const sending = this.diagnostics.record('send', 'started');
    try {
      const accepted = await session.send(text, to, attachmentIds);
      const sequence = this.diagnostics.record(
        'send',
        accepted ? 'succeeded' : 'failed',
        accepted ? 'none' : 'unknown-failure',
        this.now() - started
      );
      if (accepted && sequence !== undefined) {
        const candidates = session.messages.filter(
          (message) =>
            message.role === 'user' &&
            !message.historical &&
            !before.has(message.id)
        );
        // Ambiguous concurrent human events stay unlinked rather than guessing ownership of this send.
        if (candidates.length === 1) {
          this.link(sequence, candidates[0]);
          if (sending !== undefined) this.link(sending, candidates[0]);
          for (const captured of capture) this.link(captured, candidates[0]);
        }
      }
      return accepted;
    } catch (error) {
      this.diagnostics.record(
        'send',
        'failed',
        'unknown-failure',
        this.now() - started
      );
      throw error;
    }
  }

  async speak(
    adapter: DirectCallSpeechAdapter,
    reply: DirectCallSpeech,
    signal: AbortSignal,
    messages: readonly DirectCallMessage[]
  ): Promise<void> {
    const human = messages.find(
      (message) =>
        message.role === 'user' &&
        message.conversationId === reply.conversationId &&
        message.humanTurnId === reply.humanTurnId &&
        Number.isSafeInteger(message.humanTurnId) &&
        message.humanTurnId! > 0
    );
    // Copy only correlation fields; do not retain a second copy of human content during playback.
    const correlation = human && {
      conversationId: human.conversationId,
      turnId: human.turnId,
    };
    const report = (
      outcome: 'started' | 'succeeded' | 'cancelled' | 'failed',
      duration = 0
    ) => {
      const sequence = this.diagnostics.record(
        outcome === 'started' ? 'playback-start' : 'playback-stop',
        outcome,
        outcome === 'failed'
          ? 'unknown-failure'
          : outcome === 'cancelled'
            ? 'cancelled'
            : 'none',
        duration
      );
      if (
        sequence !== undefined &&
        correlation &&
        Number.isSafeInteger(correlation.turnId) &&
        correlation.turnId! > 0
      )
        this.diagnostics.link(
          sequence,
          correlation.conversationId,
          correlation.turnId!
        );
    };
    const started = this.now();
    report('started');
    try {
      await adapter.speak(reply, signal);
      report(signal.aborted ? 'cancelled' : 'succeeded', this.now() - started);
    } catch (error) {
      report(
        signal.aborted || this.interrupted(error) ? 'cancelled' : 'failed',
        this.now() - started
      );
      throw error;
    }
  }

  observe(messages: readonly DirectCallMessage[]): void {
    for (const message of messages) {
      if (
        message.role !== 'assistant' ||
        message.historical ||
        this.seenReplies.has(message.id) ||
        this.seenReplies.size >= 512
      )
        continue;
      const human = messages.find(
        (candidate) =>
          candidate.role === 'user' &&
          candidate.conversationId === message.conversationId &&
          candidate.humanTurnId === message.humanTurnId &&
          Number.isSafeInteger(candidate.humanTurnId) &&
          candidate.humanTurnId! > 0
      );
      if (!human) continue;
      this.seenReplies.add(message.id);
      const sequence = this.diagnostics.record('reply', 'succeeded');
      if (sequence !== undefined) this.link(sequence, human);
    }
  }

  private link(sequence: number, human: DirectCallMessage): void {
    if (Number.isSafeInteger(human.turnId) && human.turnId! > 0)
      this.diagnostics.link(sequence, human.conversationId, human.turnId!);
  }
}
